import { MoreHorizontal, Edit, Save, EyeOff } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { ReportPostDialog } from '../report-post';
import useBookmark from '../../hooks/use-bookmark/useBookMark';
import { IPostModel } from '../../models';
import DeletePost from '../delete-post/delete-post.component';
import toast from 'react-hot-toast';
import ModalCreatePost from '../../pages/home/components/modal.create.post';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { useNavigate } from 'react-router-dom';
import { useOnClickOutside } from 'usehooks-ts';

interface IPostMenuProps {
  post: IPostModel;
  userId: string;
  removePost: (id: string) => void;
  setIsHiddenPost: (id: string) => void;
}

const PostMenu = ({
  post,
  userId,
  removePost,
  setIsHiddenPost,
}: IPostMenuProps) => {
  const [isShowMoreMenu, setShowMoreMenu] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isClose, setIsClose] = useState<boolean>(true);
  const [idDetail, setIdDetail] = useState<string>('');
  const [isReportDialogOpen, setIsReportDialogOpen] = useState<boolean>(false); // Track ReportPostDialog state
  const { toggleBookmark } = useBookmark();

  const handleEditPost = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
    setShowMoreMenu(false);
    setIsClose(false);
    setIdDetail(post?.id);
  };

  const handleClearIdDetail = () => {
    setIdDetail('');
  };

  const handleOpen = () => {
    setOpen(false);
  };

  const handleClose = (data: boolean) => {
    setIsClose(data);
  };

  const userProfile = useSelector(
    (state: RootState) => state.features.auth.profile
  );

  const navigator = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Only close menu on outside click if ReportPostDialog is not open
  useOnClickOutside(ref, () => {
    if (!isReportDialogOpen) {
      setShowMoreMenu(false);
    }
  });

  const handleMenuItemClick = (e: React.MouseEvent, callback: () => void) => {
    e.stopPropagation();
    callback();
    setShowMoreMenu(false);
  };

  return (
    <>
      <div ref={ref} className="relative flex items-start z-10">
        <button
          className="button-icon w-10 h-10"
          type="button"
          onClick={() => {
            if (!userProfile) {
              navigator('/account/login');
            } else {
              setShowMoreMenu((prev) => !prev);
            }
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 40 40"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="40" height="40" rx="20" fill="#EAEFFA" />
            <path
              d="M13 18C11.9 18 11 18.9 11 20C11 21.1 11.9 22 13 22C14.1 22 15 21.1 15 20C15 18.9 14.1 18 13 18Z"
              stroke="#657384"
              strokeWidth="1.5"
            />
            <path
              d="M27 18C25.9 18 25 18.9 25 20C25 21.1 25.9 22 27 22C28.1 22 29 21.1 29 20C29 18.9 28.1 18 27 18Z"
              stroke="#657384"
              strokeWidth="1.5"
            />
            <path
              d="M20 18C18.9 18 18 18.9 18 20C18 21.1 18.9 22 20 22C21.1 22 22 21.1 22 20C22 18.9 21.1 18 20 18Z"
              stroke="#657384"
              strokeWidth="1.5"
            />
          </svg>
        </button>

        {isShowMoreMenu && (
          <div
            className="absolute top-full right-0 rounded-md w-max bg-white dark:bg-black text-slate-800 dark:text-slate-50 shadow-[0px_4px_20px_rgba(0,0,0,0.1)] p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <ul>
              {post?.userId === userId && (
                <>
                  {/* Bài hợp tác quảng cáo (Brand Deal) đăng hộ doanh nghiệp — không sửa được nội dung. */}
                  {!post?.isSponsored && (
                    <li>
                      <button
                        className="rounded-md hover:bg-gray-200 flex items-center gap-2 w-full px-4 py-2 hover:bg-gray-1 dark:hover:bg-gray-10"
                        onClick={handleEditPost}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 22H3C2.59 22 2.25 21.66 2.25 21.25C2.25 20.84 2.59 20.5 3 20.5H21C21.41 20.5 21.75 20.84 21.75 21.25C21.75 21.66 21.41 22 21 22Z"
                            fill="#657384"
                          />
                          <path
                            d="M19.0201 3.47967C17.0801 1.53967 15.1801 1.48967 13.1901 3.47967L11.9801 4.68967C11.8801 4.78967 11.8401 4.94967 11.8801 5.08967C12.6401 7.73967 14.7601 9.85967 17.4101 10.6197C17.4501 10.6297 17.4901 10.6397 17.5301 10.6397C17.6401 10.6397 17.7401 10.5997 17.8201 10.5197L19.0201 9.30967C20.0101 8.32967 20.4901 7.37967 20.4901 6.41967C20.5001 5.42967 20.0201 4.46967 19.0201 3.47967Z"
                            fill="#657384"
                          />
                          <path
                            d="M15.6101 11.5298C15.3201 11.3898 15.0401 11.2498 14.7701 11.0898C14.5501 10.9598 14.3401 10.8198 14.1301 10.6698C13.9601 10.5598 13.7601 10.3998 13.5701 10.2398C13.5501 10.2298 13.4801 10.1698 13.4001 10.0898C13.0701 9.8098 12.7001 9.4498 12.3701 9.0498C12.3401 9.0298 12.2901 8.9598 12.2201 8.8698C12.1201 8.7498 11.9501 8.5498 11.8001 8.3198C11.6801 8.1698 11.5401 7.9498 11.4101 7.7298C11.2501 7.4598 11.1101 7.1898 10.9701 6.9098C10.9489 6.86441 10.9284 6.81924 10.9085 6.77434C10.7609 6.44102 10.3263 6.34358 10.0685 6.60133L4.34007 12.3298C4.21007 12.4598 4.09007 12.7098 4.06007 12.8798L3.52007 16.7098C3.42007 17.3898 3.61007 18.0298 4.03007 18.4598C4.39007 18.8098 4.89007 18.9998 5.43007 18.9998C5.55007 18.9998 5.67007 18.9898 5.79007 18.9698L9.63007 18.4298C9.81007 18.3998 10.0601 18.2798 10.1801 18.1498L15.9014 12.4285C16.1609 12.1689 16.063 11.7235 15.7254 11.5794C15.6874 11.5632 15.649 11.5467 15.6101 11.5298Z"
                            fill="#657384"
                          />
                        </svg>
                        <span className="text-base font-normal text-[#18191E]">
                          Chỉnh sửa bài viết
                        </span>
                      </button>
                      <div className="text-xs text-center font-medium" />
                    </li>
                  )}
                  <li>
                    <DeletePost removePost={removePost} postId={post?.id} />
                    <div className="text-xs text-center font-medium" />
                  </li>
                </>
              )}
              <li>
                <button
                  className="rounded-md hover:bg-gray-200 flex items-center w-full px-4 py-2 gap-2 hover:bg-gray-1 dark:hover:bg-gray-10"
                  onClick={(e) =>
                    handleMenuItemClick(e, () => {
                      post?.id && toggleBookmark(post?.id);
                      toast.success('Đã lưu bài viết');
                    })
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.89 5.87988H5.11C3.4 5.87988 2 7.27988 2 8.98988V20.3499C2 21.7999 3.04 22.4199 4.31 21.7099L8.24 19.5199C8.66 19.2899 9.34 19.2899 9.75 19.5199L13.68 21.7099C14.96 22.4099 16 21.7999 16 20.3499V8.98988C16 7.27988 14.6 5.87988 12.89 5.87988Z"
                      fill="#657384"
                    />
                    <path
                      d="M22.0001 5.11V16.47C22.0001 17.92 20.9601 18.53 19.6901 17.83L17.7601 16.75C17.6001 16.66 17.5001 16.49 17.5001 16.31V8.99C17.5001 6.45 15.4301 4.38 12.8901 4.38H8.82008C8.45008 4.38 8.19008 3.99 8.36008 3.67C8.88008 2.68 9.92008 2 11.1101 2H18.8901C20.6001 2 22.0001 3.4 22.0001 5.11Z"
                      fill="#657384"
                    />
                  </svg>
                  <span className="text-base font-normal text-[#18191E]">
                    Lưu bài
                  </span>
                </button>
              </li>
              <li>
                <button
                  className="rounded-md hover:bg-gray-200 flex items-center w-full px-4 py-2 gap-2 hover:bg-gray-1 dark:hover:bg-gray-10"
                  onClick={(e) =>
                    handleMenuItemClick(e, () => {
                      post?.id && setIsHiddenPost(post?.id);
                    })
                  }
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.2594 9.40697C20.9684 9.01955 20.6573 8.65739 20.3362 8.32049C19.9649 7.92464 19.2624 7.89096 18.8409 8.24469L15.8303 10.7714C16.0511 11.3273 16.0912 11.9674 15.8905 12.6327C15.5393 13.8203 14.3953 14.7804 12.9803 15.0752C12.1875 15.2436 11.4249 15.21 10.7625 15.0247C10.7625 15.0247 9.32748 16.2291 8.29385 17.0966C7.79209 17.5177 7.95265 18.2588 8.62502 18.4778C9.69879 18.8231 10.8127 19 11.9567 19C13.743 19 15.4791 18.562 17.0647 17.7451C18.6803 16.9028 20.1355 15.6648 21.3096 14.0898C22.2629 12.818 22.2128 10.6787 21.2594 9.40697Z"
                      fill="#657384"
                    />
                    <path
                      d="M13.2209 10.5544L10.5544 13.2209C10.2178 12.8777 10 12.4025 10 11.8877C10 10.8514 10.8448 10 11.8877 10C12.4025 10 12.8777 10.2178 13.2209 10.5544Z"
                      fill="#657384"
                    />
                    <path
                      d="M18 6.87854L14.6622 9.62348C13.9434 9.02429 12.9489 8.66802 11.8462 8.66802C9.64062 8.66802 7.86831 10.1336 7.86831 11.9393C7.86831 12.8462 8.31139 13.664 9.03015 14.2551L5.70215 17H5.69231C4.59938 16.2713 3.59508 15.3401 2.73846 14.2389C1.75385 12.9676 1.75385 10.9028 2.73846 9.63158C3.88062 8.15789 5.27877 7 6.83446 6.20648C8.39015 5.42915 10.0935 5 11.8462 5C14.0418 5 16.1686 5.66397 18 6.87854Z"
                      fill="#657384"
                    />
                    <path
                      d="M13.85 11.9802C13.7582 13.0124 12.8413 13.7857 11.8026 13.6933C11.7632 13.6898 11.7303 13.6869 11.692 13.6703L13.8468 11.8673C13.8564 11.9079 13.8535 11.9408 13.85 11.9802Z"
                      fill="#657384"
                    />
                    <path
                      d="M22.6719 3.22321C22.4025 2.89552 21.9148 2.84799 21.5871 3.11748L1.43344 19.7007C1.10575 19.9702 1.05822 20.4579 1.3277 20.7856C1.46342 20.9395 1.64476 21.0375 1.84382 21.0569C2.04287 21.0763 2.23974 21.0152 2.40358 20.8804L22.5672 4.29811C22.9048 4.0296 22.9514 3.55186 22.6719 3.22321Z"
                      fill="#657384"
                    />
                  </svg>
                  <span className="text-base font-normal text-[#18191E]">
                    Ẩn bài viết
                  </span>
                </button>
              </li>
              <li>
                <ReportPostDialog
                  postId={post?.id}
                  onwerPostReportId={post?.userId}
                  onOpenChange={setIsReportDialogOpen} // Pass callback to track dialog state
                />
              </li>
            </ul>
          </div>
        )}
      </div>
      {open && (
        <ModalCreatePost
          onClearIdDetail={handleClearIdDetail}
          isOpen={open}
          onOpen={handleOpen}
          onClose={handleClose}
          isClose={isClose}
          isEdit={idDetail}
        />
      )}
    </>
  );
};

export default PostMenu;
